import type { ExperienceSocialConnector } from '@logto/schemas';
import classNames from 'classnames';
import { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import SocialLinkButton from '@/components/Button/SocialLinkButton';
import { usePromiseConfirmModal } from '@/hooks/use-confirm-modal';
import useNativeMessageListener, {
  NATIVE_AUTH_CANCELLED_EVENT,
} from '@/hooks/use-native-message-listener';
import { useUnlockOnReturn } from '@/hooks/use-unlock-on-return';
import { trackEvent } from '@/utils/analytics';
import { isInAppBrowser } from '@/utils/in-app-browser';
import { setLastUsedSigninMethod, getLastUsedSigninMethod } from '@/utils/last-used-signin';
import { getLogoUrl } from '@/utils/logo';

import styles from './index.module.scss';
import useSocial from './use-social';

type Props = {
  readonly className?: string;
  readonly socialConnectors?: ExperienceSocialConnector[];
};

const SocialSignInList = ({ className, socialConnectors = [] }: Props) => {
  const { invokeSocialSignIn, theme } = useSocial();
  const { t } = useTranslation();
  const { show: showModal } = usePromiseConfirmModal();
  useNativeMessageListener();

  const [loadingConnectorId, setLoadingConnectorId] = useState<string>();
  const isProcessingRef = useRef(false);
  const lastUsedMethod = useMemo(() => getLastUsedSigninMethod(), []);
  const isInApp = useMemo(() => isInAppBrowser(), []);

  // Recover the social sign-in button when the user returns from a cancelled
  // native auth flow.
  // - Primary: explicit `user_cancelled` message from the iOS SDK (reliable
  //   even when ASWebAuthenticationSession does not flip visibilitychange).
  // - Fallback: document visibilitychange after a grace period, which catches
  //   Android Custom Tabs back navigation and other native shells that don't
  //   post a cancellation signal.
  const handleUnlock = useCallback(() => {
    setLoadingConnectorId(undefined);
    isProcessingRef.current = false;
  }, []);
  useEffect(() => {
    window.addEventListener(NATIVE_AUTH_CANCELLED_EVENT, handleUnlock);
    return () => {
      window.removeEventListener(NATIVE_AUTH_CANCELLED_EVENT, handleUnlock);
    };
  }, [handleUnlock]);
  useUnlockOnReturn(Boolean(loadingConnectorId), handleUnlock);

  const handleClick = async (connector: ExperienceSocialConnector) => {
    // Google blocks OAuth in in-app browsers (LINE, Facebook, etc.) with "403: disallowed_useragent".
    // Show a modal explaining the issue instead of attempting the sign-in flow.
    if (isInApp && connector.target === 'google') {
      await showModal({
        type: 'confirm',
        className: styles.inAppBrowserModal,
        ModalContent: () => (
          <div>
            <div className={styles.modalTitle}>
              {String(t('description.in_app_browser_google_blocked_title', { defaultValue: '' }))}
            </div>
            <div className={styles.modalDescription}>
              {String(t('description.in_app_browser_google_blocked', { defaultValue: '' }))}
            </div>
          </div>
        ),
        // @ts-expect-error -- custom i18n key not in upstream @logto/phrases-experience types
        confirmText: 'action.ok',
      });

      return;
    }

    // UseRef updates synchronously, preventing rapid/double clicks
    if (isProcessingRef.current) {
      return;
    }
    isProcessingRef.current = true;

    trackEvent(
      'social_sign_in',
      'clicked',
      { eventId: 'login_form_social_sign_in_clicked' },
      { connectorId: connector.id }
    );
    setLoadingConnectorId(connector.id);
    setLastUsedSigninMethod(connector.id);

    const success = await invokeSocialSignIn(connector);

    // Only unlock on failure so the user can retry.
    // On success the native auth flow takes over (or the page redirects),
    // so keep the lock to prevent duplicate invocations.
    if (!success) {
      setLoadingConnectorId(undefined);
      isProcessingRef.current = false;
    }
  };

  return (
    <div className={classNames(styles.socialLinkList, className)}>
      {socialConnectors.map((connector) => {
        const { id, name, logo: logoUrl, logoDark: darkLogoUrl, target } = connector;

        return (
          <SocialLinkButton
            key={id}
            className={styles.socialLinkButton}
            name={name}
            logo={getLogoUrl({ theme, logoUrl, darkLogoUrl })}
            target={target}
            isLoading={loadingConnectorId === id}
            onClick={() => {
              void handleClick(connector);
            }}
            isLastUsed={id === lastUsedMethod}
          />
        );
      })}
    </div>
  );
};

export default SocialSignInList;
